import matter from 'gray-matter';

import { estimateReadingTime, extractHeadings } from './markdown';
import { getCategoryBySlug } from '@/data/categories';

// Registre = FRONTMATTER SEUL, pré-extrait au build par
// scripts/build-articles-index.mjs. Le corps des articles n'est PAS ici.
//
// Avant (jusqu'au 2026-07-19) : un `import.meta.glob(..., eager: true)`
// chargeait le markdown COMPLET des 218 articles, dans les 3 langues, et le
// parsait avec gray-matter au chargement du module. Comme cette chaîne part de
// l'entrée (App → Layout → Header → ici), Vite en faisait 34 chunks en
// modulepreload : 994 Ko gzip sur la page d'accueil, 63 % du JS initial, pour
// un contenu dont 70 % est du corps d'article que rien au démarrage ne lit.
// Mesures et pièges : docs/perf/CHARGEMENT_INITIAL.md.
import { ARTICLES_INDEX } from '@/generated/articles-index';

// Corps des articles : glob NON-eager. Vite produit un chunk par fichier,
// chargé uniquement quand loadArticleBody() le demande.
const ARTICLE_BODIES = import.meta.glob('/content/articles/**/*.md', {
  query: '?raw',
  import: 'default',
});

const LEGAL_MODULES = import.meta.glob('/content/legal/**/*.md', {
  query: '?raw',
  import: 'default',
  eager: true,
});

const ARTICLE_REGISTRY = Object.fromEntries(
  Object.entries(ARTICLES_INDEX).map(([key, frontmatter]) => [key, { frontmatter }]),
);

/** Chemin disque d'un article, pour retrouver son corps dans le glob. */
function bodyPath(frontmatter) {
  return `/content/articles/${frontmatter.lang}/${frontmatter.category}/${frontmatter.slug}.md`;
}

/**
 * Charge le corps d'un article à la demande.
 *
 * Renvoie `{ content, headings }`, ou null si l'article est inconnu. Le
 * frontmatter reste disponible SYNCHRONEMENT via getArticle/findArticle : seul
 * le corps est asynchrone, et seule la page d'un article en a besoin.
 */
export async function loadArticleBody(lang, slug) {
  const entry = ARTICLE_REGISTRY[`${lang}/${slug}`];
  if (!entry) return null;

  // Le chemin est reconstruit depuis le frontmatter. La catégorie déclarée peut
  // différer du dossier réel (c'est arrivé sur 7 articles en juillet) : on
  // retombe alors sur une recherche par langue + slug plutôt que d'échouer.
  let loader = ARTICLE_BODIES[bodyPath(entry.frontmatter)];
  if (!loader) {
    const key = Object.keys(ARTICLE_BODIES).find(
      (p) => p.includes(`/articles/${lang}/`) && p.endsWith(`/${slug}.md`),
    );
    loader = key ? ARTICLE_BODIES[key] : null;
  }
  if (!loader) return null;

  const raw = await loader();
  const { content, data } = matter(raw);
  // Le chunk du corps contient le markdown BRUT, donc son frontmatter complet :
  // on en ressort le bloc `faq:` sans rien charger de plus. L'index FAQ global
  // (175 Ko) reste réservé à la recherche et à /questions.
  return {
    content,
    headings: extractHeadings(content),
    faq: Array.isArray(data.faq) ? data.faq : [],
  };
}

/**
 * Index des paires question/réponse, chargé À LA DEMANDE.
 *
 * Les blocs `faq:` pèsent ~64 % du frontmatter (175 Ko gzip contre 91 Ko pour
 * le reste). Ils ne servent qu'à l'ouverture de la recherche et à /questions —
 * les imposer à chaque visiteur reviendrait à annuler une bonne part du gain.
 */
export async function loadFaqIndex(lang = 'fr') {
  const { FAQ_INDEX } = await import('@/generated/faq-index');
  const rows = FAQ_INDEX[lang] ?? [];
  return rows.map((e) => ({
    id: `${e.slug}#${e.i}`,
    question: e.question,
    answer: e.answer,
    articleTitle: e.articleTitle,
    articleSlug: e.slug,
    category: e.category,
    href: `${articleHrefFromParts(e.category, e.slug, lang)}#faq-${e.i}`,
  }));
}
const LEGAL_REGISTRY = (() => {
  // Legal docs follow /content/legal/{lang}/{slug}.md
  // path.split('/') → ['', 'content', 'legal', lang, '{slug}.md']
  const out = {};
  for (const [path, raw] of Object.entries(LEGAL_MODULES)) {
    const segments = path.split('/');
    const lang = segments[3];
    const file = segments[4];
    if (!file) continue;
    const slug = file.replace(/\.md$/, '');
    const { data, content } = matter(raw);
    out[`${lang}/${slug}`] = { frontmatter: { ...data, slug, lang }, content };
  }
  return out;
})();

/** Look up an article by language + slug. Returns null when missing. */
export function getArticle(lang, slug) {
  return ARTICLE_REGISTRY[`${lang}/${slug}`] ?? null;
}

/** Look up an article by category + slug, with FR fallback when missing. */
export function findArticle({ lang, category, slug }) {
  const direct = getArticle(lang, slug);
  if (direct && direct.frontmatter.category === category) return { article: direct, fallback: false };

  // Try cross-category match in the requested language.
  if (direct) return { article: direct, fallback: false };

  // Fall back to FR (the editorial source language).
  const fr = getArticle('fr', slug);
  if (fr) return { article: fr, fallback: lang !== 'fr' };

  return { article: null, fallback: false };
}

/** Articles for a given language (sorted: pillar first, then by date desc). */
export function listArticles({ lang = 'fr', category, includeDrafts = false } = {}) {
  return Object.values(ARTICLE_REGISTRY)
    .filter((a) => a.frontmatter.lang === lang)
    .filter((a) => (includeDrafts ? true : a.frontmatter.status !== 'draft'))
    .filter((a) => (category ? a.frontmatter.category === category : true))
    .sort((a, b) => {
      if (a.frontmatter.pillar !== b.frontmatter.pillar) return a.frontmatter.pillar ? -1 : 1;
      return (b.frontmatter.date || '').localeCompare(a.frontmatter.date || '');
    });
}

/** All articles across languages — used by sitemap generation. */
export function allArticles() {
  return Object.values(ARTICLE_REGISTRY);
}

/**
 * For a given FR (master) slug, return the list of languages that actually
 * have a published translation. Used to emit hreflang only for real pages
 * (Google docs: hreflang must point to a real URL, never a 404).
 *
 * Resolves both directions:
 *   - The current article itself is one of the available languages.
 *   - Any other article whose `translationOf` matches the master slug.
 *
 * @param {string} masterSlug — the FR slug (or the translationOf value).
 * @returns {string[]} e.g. ['fr', 'en'] — always at least includes the
 *                     original language if the article exists.
 */
export function getAvailableLanguages(masterSlug) {
  if (!masterSlug) return [];
  const langs = new Set();
  for (const entry of Object.values(ARTICLE_REGISTRY)) {
    const fm = entry.frontmatter;
    if (fm.slug === masterSlug || fm.translationOf === masterSlug) {
      langs.add(fm.lang);
    }
  }
  return Array.from(langs);
}

/** Resolve `relatedArticles` slug array → article objects in the same lang. */
export function getRelated(article) {
  const slugs = article?.frontmatter?.relatedArticles ?? [];
  return slugs
    .map((s) => getArticle(article.frontmatter.lang, s))
    .filter(Boolean);
}

/**
 * Article series support — resolve the full chronology of a multi-part guide.
 *
 * Articles opt into a series by setting in frontmatter :
 *   series:
 *     slug: "visa-friendly-nations-deep-dive"   # canonical series id
 *     position: 2                                # 1-indexed position
 *     title: "Visa Friendly Nations en 5 actes"  # display title (per-lang)
 *
 * The series itself is implicit — there's no central registry. Any article
 * with a matching `series.slug` joins the chronology. Position drives the
 * sort order, ties are broken by publishedAt.
 *
 * @param {object} article — the current article (frontmatter shape).
 * @returns {object|null}  — { slug, title, total, current, previous, next, parts }
 *                            or null when the article isn't part of a series.
 *
 * @example
 *   const series = getArticleSeries(currentArticle.frontmatter);
 *   if (series) {
 *     // Render "Part 2 of 5 · Visa Friendly Nations en 5 actes"
 *     // + prev/next nav cards
 *   }
 */
export function getArticleSeries(article) {
  const seriesMeta = article?.series;
  if (!seriesMeta?.slug) return null;

  const lang = article.lang;
  const parts = Object.values(ARTICLE_REGISTRY)
    .map((e) => e.frontmatter)
    .filter((fm) => fm.lang === lang && fm.series?.slug === seriesMeta.slug)
    .filter((fm) => fm.status !== 'draft')
    .sort((a, b) => {
      const pa = a.series?.position ?? 999;
      const pb = b.series?.position ?? 999;
      if (pa !== pb) return pa - pb;
      // Stable tie-breaker on publish date when position is missing
      return (a.publishedAt || a.date || '').localeCompare(b.publishedAt || b.date || '');
    });

  if (parts.length <= 1) return null; // single-part = not really a series

  const currentIndex = parts.findIndex((p) => p.slug === article.slug);
  if (currentIndex === -1) return null;

  return {
    slug: seriesMeta.slug,
    title: seriesMeta.title || seriesMeta.slug,
    total: parts.length,
    current: currentIndex + 1, // 1-indexed for human display
    previous: currentIndex > 0 ? parts[currentIndex - 1] : null,
    next: currentIndex < parts.length - 1 ? parts[currentIndex + 1] : null,
    parts,
  };
}

/** Pillar articles for the Home grid (top 6 by default). */
export function getPillarArticles({ lang = 'fr', limit = 6 } = {}) {
  return listArticles({ lang })
    .filter((a) => a.frontmatter.pillar)
    .slice(0, limit);
}

/** Latest articles across all categories. */
export function getLatestArticles({ lang = 'fr', limit = 6 } = {}) {
  return listArticles({ lang }).slice(0, limit);
}

/** Look up a legal page by language + slug. */
export function getLegalDoc(lang, slug) {
  // Fall back to FR when the requested translation is missing.
  return LEGAL_REGISTRY[`${lang}/${slug}`] ?? LEGAL_REGISTRY[`fr/${slug}`] ?? null;
}

/** URL publique d'un article, segment de catégorie traduit compris. */
function articleHrefFromParts(category, slug, lang) {
  const cat = getCategoryBySlug(category);
  const catSlug = cat?.pathSlug[lang] ?? category;
  const prefix = lang === 'fr' ? '' : `/${lang}`;
  return `${prefix}/${catSlug}/${slug}`;
}

function articleHref(frontmatter, lang) {
  return articleHrefFromParts(frontmatter.category, frontmatter.slug, lang);
}

/** Build a search index payload (used by SearchBox + Phase B Fuse.js index). */
export function buildSearchIndex(lang = 'fr') {
  return listArticles({ lang }).map(({ frontmatter }) => ({
    slug: frontmatter.slug,
    title: frontmatter.title,
    excerpt: frontmatter.excerpt,
    tags: frontmatter.tags ?? [],
    href: articleHref(frontmatter, lang),
  }));
}

